/**
 * Utilitário centralizado para chamadas à API do Gemini.
 * Contém duas camadas de proteção:
 * 1. Exponential Backoff (Atraso Exponencial) para erros 503.
 * 2. Fallback de Modelo para `gemini-3.5-flash-lite` se o principal falhar.
 */

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const fetchWithGeminiResilience = async (apiKey, payload, customConfig = {}) => {
    const MAX_RETRIES = 3;
    const PRIMARY_MODEL = "gemini-3.7-flash";
    const FALLBACK_MODEL = "gemini-3.5-flash-lite";
    
    let lastError = null;

    // Tentativas no Modelo Principal com Backoff Exponencial
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        if (attempt > 0) {
            // Atraso exponencial: 1s, 2s, 4s...
            const waitTime = Math.pow(2, attempt - 1) * 1000;
            console.warn(`[Gemini API] Erro no modelo principal. Tentativa ${attempt}/${MAX_RETRIES}. Aguardando ${waitTime}ms...`);
            await delay(waitTime);
        }

        try {
            const url = `https://generativelanguage.googleapis.com/v1beta/models/${PRIMARY_MODEL}:generateContent?key=${apiKey}`;
            
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                return response;
            }

            const status = response.status;
            
            // Tratamento específico de erro
            if (status === 503 || status === 429 || status >= 500) {
                lastError = new Error(`Erro temporário na API (Status: ${status})`);
                continue; // Aciona o backoff e tenta novamente
            } else {
                // Erros determinísticos (como 400 Bad Request, 403 Forbidden) falham imediatamente
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.error?.message || `Erro fatal na API (Status: ${status})`);
            }
        } catch (error) {
            lastError = error;
            // Se for um erro determinístico, não use o throw ainda se quiser fazer fallback de modelo,
            // mas como a requisição está mal formulada, o ideal é estourar.
            // Para simplicidade de resiliência, apenas os erros de rede e 'continues' farão o loop progredir.
            if (error.message.includes('Erro fatal na API')) {
                throw error;
            }
        }
    }

    // Camada 2: Fallback de Modelo (Plano B)
    console.error(`[Gemini API] Falha total no modelo ${PRIMARY_MODEL} após ${MAX_RETRIES} tentativas. Redirecionando para o fallback: ${FALLBACK_MODEL}`);
    
    try {
        const fallbackUrl = `https://generativelanguage.googleapis.com/v1beta/models/${FALLBACK_MODEL}:generateContent?key=${apiKey}`;
        
        const fallbackResponse = await fetch(fallbackUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (!fallbackResponse.ok) {
            const errData = await fallbackResponse.json().catch(() => ({}));
            throw new Error(errData.error?.message || `Erro fatal no modelo de fallback (Status: ${fallbackResponse.status})`);
        }

        return fallbackResponse;
    } catch (fallbackError) {
        throw new Error(`Ambas as abordagens falharam. Original: ${lastError.message}. Fallback: ${fallbackError.message}`);
    }
};
